import React, { forwardRef } from "react";
import { Input as AntInput } from "antd";
import type { InputProps as AntInputProps, InputRef } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import "./stylesInput.css";

export type InputVariant = "text" | "search";
export type InputTone = "default" | "error" | "success";

const toneToAntStatus: Record<InputTone, AntInputProps["status"]> = {
  default: undefined,
  error: "error",
  success: undefined, // antd has no native "success" status; we apply custom CSS
};

export type InputOwnProps = Omit<AntInputProps, "variant"> & {
  /** Visual treatment. `search` adds a default `<SearchOutlined />` prefix and "Search" placeholder. */
  variant?: InputVariant;
  /** Semantic state. Controls border color via antd `status` (error) or custom CSS (success). */
  tone?: InputTone;
  /**
   * Icon rendered to the left of the input. Preferred alias for antd's `prefix`.
   * If both `prefixIcon` and `prefix` are passed, `prefixIcon` wins.
   */
  prefixIcon?: React.ReactNode;
  /**
   * Icon rendered to the right of the input. Preferred alias for antd's `suffix`.
   * If both `suffixIcon` and `suffix` are passed, `suffixIcon` wins.
   */
  suffixIcon?: React.ReactNode;
};

const Input = forwardRef<InputRef, InputOwnProps>(
  (
    {
      variant = "text",
      tone = "default",
      prefixIcon,
      suffixIcon,
      prefix,
      suffix,
      placeholder,
      status,
      className = "",
      ...rest
    },
    ref,
  ) => {
    const isSearch = variant === "search";

    const resolvedPrefix =
      prefixIcon ?? prefix ?? (isSearch ? <SearchOutlined /> : undefined);
    const resolvedSuffix = suffixIcon ?? suffix;
    const resolvedPlaceholder =
      placeholder ?? (isSearch ? "Search" : undefined);
    const resolvedStatus = tone !== "default" ? toneToAntStatus[tone] : status;

    const classNames = [
      "custom-form-input",
      `pam-input--${variant}`,
      tone !== "default" ? `pam-input--${tone}` : null,
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <AntInput
        ref={ref}
        className={classNames}
        prefix={resolvedPrefix}
        suffix={resolvedSuffix}
        placeholder={resolvedPlaceholder}
        status={resolvedStatus}
        {...rest}
      />
    );
  },
);

Input.displayName = "Input";

export default Input;
